import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './WalletPage.css';
import { useAuth } from './AuthContext';

interface UserProfile {
  wallet_balance: number;
}

interface Transaction {
  id: number;
  date: string;
  amount: number;
  type: 'credit' | 'debit_withdrawal' | 'credit_id_creation' | 'credit_invest' ;
  status: 'pending' | 'completed' | 'failed';
  transaction_id: string | null;
}

const getDisplayType = (type: Transaction['type']) => {
  switch (type) {
    case 'credit':
      return 'Credit';
    case 'debit_withdrawal':
      return 'Withdrawal';
    case 'credit_invest':
      return 'Credit Invest';
    case 'credit_id_creation':
      return 'Direct ID Credit';
    default:
      const exhaustiveCheck: never = type;
      return exhaustiveCheck;
  }
};

const WalletPage: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<'all' | 'credit' | 'debit_withdrawal' | 'credit_id_creation' | 'credit_invest' >('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('all');
  const [transactionMode, setTransactionMode] = useState<'add' | 'withdraw'>('add');
  const [transactionAmount, setTransactionAmount] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [staticSummary, setStaticSummary] = useState({
    totalCredit: 0,
    totalCreditInvest: 0,
    totalDebit: 0,
    pendingCredit: 0,
    pendingDebit: 0,
    totalDirectCredit: 0,
    walletBalance: 0,
    accountNumber: null as string | null,
    panNumber: null as string | null,
  });
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [goToPage, setGoToPage] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const { user } = useAuth();

  // Admin "View As" state
  const [viewAsUser, setViewAsUser] = useState<string>('');
  const [viewAsSearchTerm, setViewAsSearchTerm] = useState<string>('');
  const [verificationMessage, setVerificationMessage] = useState<{ status: 'success' | 'error'; message: string } | null>(null);

  const fetchTransactions = async () => {
    setIsLoading(true);
    // setError(null); // Error state is not used for display
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(itemsPerPage),
        type: typeFilter,
        status: statusFilter,
      });
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (user?.role === 'admin' && viewAsUser) {
        params.append('username', viewAsUser);
      }

      const response = await fetch(`/api/wallet/transactions?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to fetch transactions.');
      }

      const data: { transactions: Transaction[], total: number } = await response.json();
      setTransactions(data.transactions);
      setTotalTransactions(data.total);
    } catch (err) {
      console.error(err instanceof Error ? err.message : 'An unknown error occurred fetching transactions.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWalletSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (user?.role === 'admin' && viewAsUser) {
        params.append('username', viewAsUser);
      }
      const response = await fetch(`/api/wallet/summary?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch wallet summary');
      }
      const data: { summary: typeof staticSummary } = await response.json();
      // Use functional update to avoid dependency on staticSummary
      setStaticSummary(data.summary);
      setBalance(data.summary.walletBalance);
    } catch (err) {
      // This is supplementary data, so we don't set the main error state
      console.error("Could not load wallet summary:", err);
    }
  }, [user?.role, viewAsUser]);


  useEffect(() => {
    // Fetch one-time data
    fetchWalletSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsUser, user?.role, fetchWalletSummary]);

  // Fetch transactions when page or filters change
  useEffect(() => {
    fetchTransactions();
    // We don't want to refetch when fetchTransactions itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, typeFilter, statusFilter, startDate, endDate, viewAsUser]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, statusFilter, startDate, endDate, itemsPerPage, viewAsUser]);

  const calculatedBalance = useMemo(() => {
    const totalCredits = 
      staticSummary.totalCredit + 
      staticSummary.totalDirectCredit +
      staticSummary.totalCreditInvest;
      
    const totalDebits = 
      staticSummary.totalDebit 
    return totalCredits - totalDebits;
  }, [staticSummary]);

  const canWithdraw = useMemo(() => {
    return staticSummary.accountNumber && staticSummary.panNumber;
  }, [staticSummary]);


  const totalPages = Math.ceil(totalTransactions / itemsPerPage);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleGoToPageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(goToPage, 10);
    if (!isNaN(page)) {
      handlePageChange(page);
    }
    setGoToPage('');
  };

  const handleTransactionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = Number(transactionAmount);

    if (transactionMode === 'withdraw') {
      // These checks should always be against the currently displayed balance and eligibility,
      // which will be the viewed user's data if in "view as" mode.
      if (amount > (balance ?? 0)) {
        setSubmitError('Withdrawal amount cannot be greater than your wallet balance.');
        return;
      }
    }

    if (amount % 100 !== 0) {
      setSubmitError('Amount must be a multiple of 100.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const requestBody = {
        amount: amount,
        type: transactionMode === 'add' ? 'credit' : 'debit_withdrawal',
        transactionId: transactionMode === 'add' ? transactionId : undefined,
      };

      if (user?.role === 'admin' && viewAsUser) {
        (requestBody as any).username = viewAsUser;
      };

      const response = await fetch('/api/wallet/transaction-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to submit request.');
      }

      setSubmitSuccess(data.message);
      setTransactionAmount('');
      setTransactionId('');
      // Refresh transactions to show the new pending request
      Promise.all([fetchTransactions(), fetchWalletSummary()]);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModeChange = (mode: 'add' | 'withdraw') => {
    setTransactionMode(mode);
    setTransactionAmount('');
    setTransactionId('');
  };

  const handleViewAsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const usernameToView = viewAsSearchTerm.trim();
    if (!usernameToView) {
      setVerificationMessage({ status: 'error', message: 'Please enter a username to view.' });
      return;
    }

    setVerificationMessage(null);

    try {
      // 1. Verify user exists
      const response = await fetch(`/api/profile?username=${encodeURIComponent(usernameToView)}`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'User not found.');
      }
      // 2. If successful, switch the view
      setViewAsUser(usernameToView);
    } catch (err) {
      // 3. If verification fails, show an error and clear any existing "view as" user
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setVerificationMessage({ status: 'error', message: errorMessage });
      setViewAsUser('');
    }
  };

  const handleClearViewAs = () => {
    setViewAsUser('');
    setViewAsSearchTerm('');
    setVerificationMessage(null);
  };

  return (
    <div className="wallet-page-container">
      <style>{`
        @media (max-width: 768px) {
          .wallet-summary-card, .transaction-filter-group {
            flex-direction: column;
            align-items: stretch;
          }
          .history-card-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
      {/* Admin "View As" Controls */}
      {user?.role === 'admin' && (
        <div className="admin-view-controls" style={{ backgroundColor: '#f0f8ff', border: '1px solid #bde0ff', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
          <form onSubmit={handleViewAsSubmit} className="view-as-form" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              value={viewAsSearchTerm}
              onChange={(e) => {
                setViewAsSearchTerm(e.target.value);
                setVerificationMessage(null); // Reset on change
              }}
              placeholder="Enter username to view their wallet"
              className="view-as-input"
              style={{ flexGrow: 1, padding: '10px 15px', fontSize: '1rem', borderRadius: '4px', border: '1px solid #ced4da' }}
            />
            <button type="submit" className="view-as-button" style={{ padding: '10px 20px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px' }}>View Wallet</button>
            {viewAsUser && (
              <button type="button" onClick={handleClearViewAs} className="view-as-button clear-view-button" style={{ padding: '10px 20px', fontSize: '1rem', fontWeight: 600, cursor: 'pointer', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' }}>
                View My Wallet
              </button>
            )}
          </form>
          {verificationMessage && (
            <div className={`verification-message ${verificationMessage.status}`} style={{ marginTop: '10px', padding: '8px 12px', borderRadius: '4px', fontSize: '0.9rem', backgroundColor: verificationMessage.status === 'error' ? '#f8d7da' : '#d4edda', color: verificationMessage.status === 'error' ? '#721c24' : '#155724' }}>
              {verificationMessage.message}
            </div>
          )}
        </div>
      )}

      {viewAsUser && user?.role === 'admin' ? (
        <h2>Viewing Wallet for: <strong>{viewAsUser}</strong></h2>
      ) : (
        <h2>My Wallet</h2>
      )}


      <div className="wallet-summary-card">
        <div className="balance-display">
          <h3>Current Balance</h3>
            <p
              className={`wallet-balance ${(balance ?? 0) >= 0 ? 'amount-credit' : 'amount-debit'}`}
            >
              ₹{(balance ?? 0).toFixed(2)}
            </p>
          <div className="dashboard-stats-summary">
            <div className="balance-summary-item">
              <span>Fund Credit:</span>
              <span className="amount-credit">
                {staticSummary.totalCredit > 0 ? `+₹${staticSummary.totalCredit.toFixed(2)}` : '-'}
              </span>
            </div>
            <div className="balance-summary-item">
              <span>Direct Referral Income:</span>
              <span className="amount-credit">
                {staticSummary.totalDirectCredit > 0 ? `+₹${staticSummary.totalDirectCredit.toFixed(2)}` : '-'}
              </span>
            </div>
            <div className="balance-summary-item">
              <span>Credit Invest:</span>
              <span className="amount-credit">
                {staticSummary.totalCreditInvest > 0 ? `+₹${staticSummary.totalCreditInvest.toFixed(2)}` : '-'}
              </span>
            </div>
            <div className="balance-summary-item">
              <span>Fund Withdrawal:</span>
              <span className="amount-debit">
                {staticSummary.totalDebit > 0 ? `-₹${staticSummary.totalDebit.toFixed(2)}` : '-'}
              </span>
            </div>         
            <div className="balance-summary-item">
              <span>Pending Credit:</span>
              <span className="amount-credit">{staticSummary.pendingCredit > 0 ? `+₹${staticSummary.pendingCredit.toFixed(2)}` : '-'}</span>
            </div>
            <div className="balance-summary-item eligibility" style={{ borderTop: 'none' }}>
              <span>Pending Debit:</span>
              <span className="amount-debit">{staticSummary.pendingDebit > 0 ? `-₹${staticSummary.pendingDebit.toFixed(2)}` : '-'}</span>
            </div>
          </div>

        </div>
        
        <div className="wallet-actions-form">
          <div className="transaction-type-group">
            <label className="radio-label">
              <input
                type="radio"
                name="transactionMode"
                value="add"
                checked={transactionMode === 'add'}
                onChange={() => handleModeChange('add')}
              />
              <span>Add Fund</span>
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="transactionMode"
                value="withdraw"
                checked={transactionMode === 'withdraw'}
                onChange={() => handleModeChange('withdraw')}
              />
              <span>Withdrawal</span>
            </label>
          </div>
          {transactionMode === 'add' && (
            <div className="payment-details-container">
              <div className="qr-code-section">
                <img
                  src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=9751550566@cub"
                  alt="QR Code for payment"
                  className="qr-code-image"
                />
                <p className="qr-caption">Scan to pay with any UPI app</p>
              </div>
              <div className="bank-details-section">
                <h4>Or, transfer to our bank account:</h4>
                <ul className="bank-details-list">
                  <li><strong>Account Name:</strong> VKS TRADERS</li>
                  <li><strong>Account Number:</strong> 512020010032503</li>
                  <li><strong>IFSC Code:</strong> CIUB0000624</li>
                  <li><strong>Bank Name:</strong> CITY UNION BANK</li>
                  <li><strong>Account Type:</strong> Current</li>
                </ul>
              </div>
            </div>
          )}

          <form onSubmit={handleTransactionSubmit} className="amount-form">
            {submitError && <div className="error-message form-error">{submitError}</div>}
            {submitSuccess && <div className="success-message form-success">{submitSuccess}</div>}
            {transactionMode === 'add' && (
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Enter UTR Number"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="transaction-id-input"
                  required
                  style={{ width: '100%' }}
                  pattern="\d{12}"
                  title="UTR Number must be 12 digits."
                />
              </div>
            )}
            <div className="form-group-inline">
              <input type="number" placeholder="Enter amount" value={transactionAmount} onChange={(e) => setTransactionAmount(e.target.value)} className="amount-input" required min="100" step="100" disabled={transactionMode === 'withdraw' && !canWithdraw} style={{ width: '100%' }} />
              <button
                type="submit"
                className={`submit-transaction-button ${transactionMode === 'add' ? 'add-mode' : 'withdraw-mode'}`}
                disabled={isSubmitting || (transactionMode === 'withdraw' && !canWithdraw)}>
                {isSubmitting ? 'Submitting...' : (transactionMode === 'add' ? 'Add Funds' : 'Withdraw Funds')}
              </button>
            </div>
            {transactionMode === 'withdraw' && !canWithdraw && (
              <div className="error-message form-note" style={{ textAlign: 'center', marginTop: '10px' }}>
                Please update your Account Number and PAN Number in your{' '}
                <a href="/profile" style={{ color: '#dc3545', fontWeight: 'bold' }}>Profile</a> to enable withdrawals.
              </div>
            )}
            {transactionMode === 'add' && (
              <p className="form-note">After payment, enter the amount and UTR Number, then click "Add Funds" to get it reflected in your wallet.</p>
            )}
            {transactionMode === 'withdraw' && (
              <p className="form-note">Note: A transaction fee of 15% will be deducted from your withdrawal amount.</p>
            )}
          </form>
        </div>
      </div>

      <div className="transaction-history-card">
        <div className="history-card-header">
          <h3>Transaction History</h3>
          <div className="transaction-filter-group">
            <div className="filter-control">
              <label htmlFor="typeFilter">Type:</label>
              <select id="typeFilter" value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="filter-select">
                  <option value="all">All Types</option>
                  <option value="credit">Credit</option>
                  <option value="debit_withdrawal">Withdrawal</option>
                  <option value="credit_invest">Credit Inverst</option>
                  <option value="credit_id_creation">Direct ID Credit</option>
              </select>
            </div>
            <div className="filter-control">
              <label htmlFor="statusFilter">Status:</label>
              <select id="statusFilter" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="filter-select">
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
              </select>
            </div>
            <div className="filter-control">
              <label htmlFor="startDate">From:</label>
              <input type="date" id="startDate" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="date-input" />
            </div>
            <div className="filter-control">
              <label htmlFor="endDate">To:</label>
              <input type="date" id="endDate" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="date-input" />
            </div>
          </div>
        </div>

        {isLoading ? (
          <p>Loading transactions...</p>
        ) : transactions.length > 0 ? (
          <>
            <table className="transaction-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Txn ID</th>
                  <th>Date & Time</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Ref ID</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, index) => (
                  <tr key={t.id}>
                    <td data-label="S.No.">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td data-label="Txn ID">{t.id}</td>
                    <td data-label="Date & Time">{new Date(t.date).toLocaleString()}</td>
                    <td data-label="Type"><span className={`type-badge type-${t.type}`}>{getDisplayType(t.type)}</span></td>
                    <td data-label="Amount" className={`transaction-amount ${t.type.startsWith('credit')  ? 'amount-credit' : 'amount-debit'}`}>
                      {t.type.startsWith('credit') ? (
                        `+₹${t.amount.toFixed(2)}`
                      ) : t.type === 'debit_withdrawal' ? (
                        <div>
                          <div>-₹{t.amount.toFixed(2)}</div>
                        </div>
                      ) : ( // debit_id_creation or other debits
                        `-₹${t.amount.toFixed(2)}`
                      )}
                    </td>
                    <td data-label="Ref ID">
                      {t.transaction_id || 'N/A'}
                    </td>
                    <td data-label="Status">
                      <span className={`status-badge status-${t.status.toLowerCase()}`}>{t.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalTransactions > 0 && (
              <div className="pagination-controls">
                <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
                  &laquo; First
                </button>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                  &lsaquo; Previous
                </button>
                <span className="page-info">Page {currentPage} of {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                  Next &rsaquo;
                </button>
                <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>
                  Last &raquo;
                </button>
                <form onSubmit={handleGoToPageSubmit} className="go-to-page-form">
                  <input
                    type="number"
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    className="page-input"
                    min="1"
                    max={totalPages}
                    placeholder="Page"
                  />
                  <button type="submit">Go</button>
                </form>
                <div className="items-per-page-selector">
                  <span>Items per page:</span>
                  <select value={itemsPerPage} onChange={e => setItemsPerPage(Number(e.target.value))} className="filter-select">
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={500}>500</option>
                    <option value={1000}>1000</option>
                  </select>
                </div>
              </div>
            )}
          </>
        ) : (
          <p>No transaction history found for the selected filter.</p>
        )}
        <style>{`
          @media (max-width: 768px) {
            .transaction-table { border: 0; }
            .transaction-table thead {
              border: none;
              clip: rect(0 0 0 0);
              height: 1px;
              margin: -1px;
              overflow: hidden;
              padding: 0;
              position: absolute;
              width: 1px;
            }
            .transaction-table tr {
              border-bottom: 3px solid #ddd;
              display: block;
              margin-bottom: .625em;
            }
            .transaction-table td {
              border-bottom: 1px solid #ddd;
              display: block;
              font-size: .8em;
              text-align: right;
            }
            .transaction-table td::before {
              content: attr(data-label);
              float: left;
              font-weight: bold;
              text-transform: uppercase;
            }
            .mobile-only-fee { display: block; }
            .fee-details strong { display: block; margin-top: 4px; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default WalletPage;